import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Truck, ArrowDownLeft, RefreshCw } from 'lucide-react';
import WarehouseMainDashboard from './WarehouseMainDashboard';
import LoadingRequestsView from './LoadingRequestsView';
import CreateLoadingRequestModal from './CreateLoadingRequestModal';
import LoadingRequestProcessingModal from './LoadingRequestProcessingModal';
import LoadingRequestRevisionModal from './LoadingRequestRevisionModal';
import OffloadRequestsView from './OffloadRequestsView';
import CreateOffloadRequestModal from './CreateOffloadRequestModal';
import OffloadRequestProcessingModal from './OffloadRequestProcessingModal';
import TransferHistoryView from './TransferHistoryView';
import WarehousePdfModal from './WarehousePdfModal';

import {
    getWarehouseDashboardStats,
    getLoadingRequests,
    saveLoadingRequest,
    completeLoadingRequest,
    submitLoadingRevision,
    getOffloadRequests,
    saveOffloadRequest,
    completeOffloadRequest,
    getTransferHistory,
    deleteDraftRequest
} from '../../../../../services/api/warehouseApi';

const WarehouseManagementContainer = ({ currentRole = 'WAREHOUSE_PERSON', initialSubView = 'dashboard', onViewChange, supplierLogoUrl, companyName }) => {
    const [activeTab, setActiveTab] = useState(initialSubView);

    useEffect(() => {
        if (initialSubView) {
            setActiveTab(initialSubView);
        }
    }, [initialSubView]);

    // Auto-scroll active tab into center view on mobile navigation
    useEffect(() => {
        if (!activeTab) return;
        const timer = setTimeout(() => {
            const activeBtn = document.querySelector(`[data-tab-id="${activeTab}"]`);
            if (activeBtn) {
                activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [activeTab]);

    const handleTabSwitch = (tab, e) => {
        const viewId = tab === 'dashboard' ? 'warehouse-dashboard' : tab;
        setActiveTab(tab);
        if (e && e.currentTarget) {
            e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
        if (onViewChange) {
            onViewChange(viewId);
        }
    };

    // Data States
    const [stats, setStats] = useState({});
    const [loadingRequests, setLoadingRequests] = useState([]);
    const [offloadRequests, setOffloadRequests] = useState([]);
    const [transferHistory, setTransferHistory] = useState([]);

    // Modal States
    const [showCreateLoadingModal, setShowCreateLoadingModal] = useState(false);
    const [selectedLoadingReq, setSelectedLoadingReq] = useState(null);
    const [showRevisionModal, setShowRevisionModal] = useState(false);
    const [revisionTargetReq, setRevisionTargetReq] = useState(null);

    const [showCreateOffloadModal, setShowCreateOffloadModal] = useState(false);
    const [selectedOffloadReq, setSelectedOffloadReq] = useState(null);

    const [pdfDocument, setPdfDocument] = useState(null);
    const [pdfType, setPdfType] = useState('loading');
    const [showPdfModal, setShowPdfModal] = useState(false);

    // Refresh Data
    const refreshData = async () => {
        try {
            const [statsRes, loadingRes, offloadRes, transferRes] = await Promise.all([
                getWarehouseDashboardStats(),
                getLoadingRequests(),
                getOffloadRequests(),
                getTransferHistory()
            ]);
            setStats(statsRes || {});
            setLoadingRequests(loadingRes || []);
            setOffloadRequests(offloadRes || []);
            setTransferHistory(transferRes || []);
        } catch (e) {
            console.error('Error fetching warehouse data', e);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    // Handlers
    const handleSaveLoadingDraft = async (reqData) => {
        const res = await saveLoadingRequest(reqData);
        await refreshData();
        return res;
    };

    const handleSubmitLoadingRequest = async (reqData) => {
        const res = await saveLoadingRequest(reqData);
        await refreshData();
        return res;
    };

    const handleCompleteLoading = async (id, warehouseData) => {
        const res = await completeLoadingRequest(id, warehouseData);
        await refreshData();
    };

    const handleSubmitRevision = async (revisionPayload) => {
        const res = await submitLoadingRevision(revisionPayload.requestId, revisionPayload);
        await refreshData();
        return res;
    };

    const handleSaveOffloadDraft = async (reqData) => {
        const res = await saveOffloadRequest(reqData);
        await refreshData();
        return res;
    };

    const handleSubmitOffloadRequest = async (reqData) => {
        const res = await saveOffloadRequest(reqData);
        await refreshData();
        return res;
    };

    const handleCompleteOffload = async (id, warehouseData) => {
        await completeOffloadRequest(id, warehouseData);
        await refreshData();
    };

    const handleDeleteDraft = async (id, type) => {
        if (window.confirm('Are you sure you want to delete this draft request?')) {
            await deleteDraftRequest(id, type);
            await refreshData();
        }
    };

    const handlePrintPdf = (doc, type = 'loading') => {
        setPdfDocument(doc);
        setPdfType(type);
        setShowPdfModal(true);
        // Clear active detail modal targets so there are no stacked popups behind the PDF
        setSelectedLoadingReq(null);
        setSelectedOffloadReq(null);
    };

    const handleViewRequestFromDashboard = (req, type) => {
        if (type === 'loading') {
            setSelectedLoadingReq(req);
        } else {
            setSelectedOffloadReq(req);
        }
    };
    const pendingLoadingCount = loadingRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'SUBMITTED' || s === 'PENDING APPROVAL' || s === 'PENDING' || s === 'REVISION PENDING';
    }).length;

    const pendingOffloadCount = offloadRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'SUBMITTED' || s === 'PENDING RETURN' || s === 'PENDING';
    }).length;

    return (
        <div className="h-full overflow-y-auto px-3 sm:px-6 pt-3 pb-28 md:pb-8 space-y-5">
            {/* Warehouse Management Sub-Header Tab Bar */}
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                <button
                    data-tab-id="dashboard"
                    onClick={(e) => handleTabSwitch('dashboard', e)}
                    className={`min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                        activeTab === 'dashboard'
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60'
                    }`}
                >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>Warehouse</span>
                </button>
                <button
                    data-tab-id="loading-requests"
                    onClick={(e) => handleTabSwitch('loading-requests', e)}
                    className={`min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                        activeTab === 'loading-requests'
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60'
                    }`}
                >
                    <Truck className="w-4 h-4" />
                    <span>Loading Request</span>
                    {pendingLoadingCount > 0 && (
                        <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
                            activeTab === 'loading-requests' ? 'bg-white text-blue-600' : 'bg-amber-500 text-white'
                        }`}>
                            {pendingLoadingCount}
                        </span>
                    )}
                </button>
                <button
                    data-tab-id="offload-requests"
                    onClick={(e) => handleTabSwitch('offload-requests', e)}
                    className={`min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                        activeTab === 'offload-requests'
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60'
                    }`}
                >
                    <ArrowDownLeft className="w-4 h-4" />
                    <span>Offload Request</span>
                    {pendingOffloadCount > 0 && (
                        <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
                            activeTab === 'offload-requests' ? 'bg-white text-blue-600' : 'bg-blue-500 text-white'
                        }`}>
                            {pendingOffloadCount}
                        </span>
                    )}
                </button>
                <button
                    data-tab-id="transfer-history"
                    onClick={(e) => handleTabSwitch('transfer-history', e)}
                    className={`min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                        activeTab === 'transfer-history'
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60'
                    }`}
                >
                    <RefreshCw className="w-4 h-4" />
                    <span>Transfer History</span>
                </button>
            </div>

            {/* Active View Renderer */}
            {activeTab === 'dashboard' && (
                <WarehouseMainDashboard
                    stats={stats}
                    loadingRequests={loadingRequests}
                    offloadRequests={offloadRequests}
                    transferHistory={transferHistory}
                    onNavigateView={(view) => handleTabSwitch(view)}
                    onViewRequest={handleViewRequestFromDashboard}
                />
            )}

            {activeTab === 'loading-requests' && (
                <LoadingRequestsView
                    requests={loadingRequests}
                    currentRole={currentRole}
                    onCreateNew={() => {
                        setSelectedLoadingReq(null);
                        setShowCreateLoadingModal(true);
                    }}
                    onViewRequest={(req) => setSelectedLoadingReq(req)}
                    onEditRequest={(req) => {
                        setSelectedLoadingReq(req);
                        setShowCreateLoadingModal(true);
                    }}
                    onRequestRevision={(req) => {
                        setRevisionTargetReq(req);
                        setShowRevisionModal(true);
                    }}
                    onPrintPdf={(req) => handlePrintPdf(req, 'loading')}
                    onDeleteDraft={(id) => handleDeleteDraft(id, 'loading')}
                />
            )}

            {activeTab === 'offload-requests' && (
                <OffloadRequestsView
                    requests={offloadRequests}
                    currentRole={currentRole}
                    onCreateNew={() => {
                        setSelectedOffloadReq(null);
                        setShowCreateOffloadModal(true);
                    }}
                    onViewRequest={(req) => setSelectedOffloadReq(req)}
                    onEditRequest={(req) => {
                        setSelectedOffloadReq(req);
                        setShowCreateOffloadModal(true);
                    }}
                    onPrintPdf={(req) => handlePrintPdf(req, 'offload')}
                    onDeleteDraft={(id) => handleDeleteDraft(id, 'offload')}
                />
            )}

            {activeTab === 'transfer-history' && (
                <TransferHistoryView
                    transfers={transferHistory}
                    onViewTransfer={(t) => handlePrintPdf(t, 'transfer')}
                    onPrintPdf={(t) => handlePrintPdf(t, 'transfer')}
                />
            )}

            {/* Modals */}
            <CreateLoadingRequestModal
                isOpen={showCreateLoadingModal}
                initialData={selectedLoadingReq}
                onClose={() => setShowCreateLoadingModal(false)}
                onSaveDraft={handleSaveLoadingDraft}
                onSubmitRequest={handleSubmitLoadingRequest}
            />

            {showRevisionModal && revisionTargetReq && (
                <LoadingRequestRevisionModal
                    isOpen={showRevisionModal}
                    request={revisionTargetReq}
                    onClose={() => {
                        setShowRevisionModal(false);
                        setRevisionTargetReq(null);
                    }}
                    onSubmitRevision={handleSubmitRevision}
                />
            )}

            {selectedLoadingReq && !showCreateLoadingModal && (
                <LoadingRequestProcessingModal
                    isOpen={!!selectedLoadingReq}
                    request={selectedLoadingReq}
                    currentRole={currentRole}
                    onClose={() => setSelectedLoadingReq(null)}
                    onComplete={handleCompleteLoading}
                    onEditRequest={(req) => {
                        setSelectedLoadingReq(req);
                        setShowCreateLoadingModal(true);
                    }}
                    onRequestRevision={(req) => {
                        setSelectedLoadingReq(null);
                        setRevisionTargetReq(req);
                        setShowRevisionModal(true);
                    }}
                    onPrintPdf={(req) => handlePrintPdf(req, 'loading')}
                    onRefreshData={refreshData}
                />
            )}

            <CreateOffloadRequestModal
                isOpen={showCreateOffloadModal}
                initialData={selectedOffloadReq}
                onClose={() => setShowCreateOffloadModal(false)}
                onSaveDraft={handleSaveOffloadDraft}
                onSubmitRequest={handleSubmitOffloadRequest}
            />

            {selectedOffloadReq && !showCreateOffloadModal && (
                <OffloadRequestProcessingModal
                    isOpen={!!selectedOffloadReq}
                    request={selectedOffloadReq}
                    currentRole={currentRole}
                    onClose={() => setSelectedOffloadReq(null)}
                    onComplete={handleCompleteOffload}
                    onEditRequest={(req) => {
                        setSelectedOffloadReq(req);
                        setShowCreateOffloadModal(true);
                    }}
                    onPrintPdf={(req) => handlePrintPdf(req, 'offload')}
                />
            )}

            <WarehousePdfModal
                isOpen={showPdfModal}
                documentData={pdfDocument}
                documentType={pdfType}
                supplierLogoUrl={supplierLogoUrl}
                companyName={companyName}
                onClose={() => setShowPdfModal(false)}
            />
        </div>
    );
};

export default WarehouseManagementContainer;
